import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Container, CircularProgress, Alert, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons';
import useChallanAnalytics from '../hooks/useChallanAnalytics';
import ChallanFilter from '../components/challan-analytics/ChallanFilter';
import ChallanSummaryCards from '../components/challan-analytics/ChallanSummaryCards';
import ChallanProductsTable from '../components/challan-analytics/ChallanProductsTable';
import ChallanCustomersTable from '../components/challan-analytics/ChallanCustomersTable';
import ChallanDailyTable from '../components/challan-analytics/ChallanDailyTable';
import ChallanListTable from '../components/challan-analytics/ChallanListTable';
import Pagination from '../components/common/Pagination';
import userService from '../services/userService';
import useAuth from '../hooks/useAuth';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 50;

const getDefaultDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    return { from: today, to: today };
};

const ChallanAnalyticsPage = () => {
    const { t } = useTranslation();
    const TABS = [
        { id: 'summary',   label: t('challanAnalytics.tabs.summary') },
        { id: 'products',  label: t('challanAnalytics.tabs.products') },
        { id: 'customers', label: t('challanAnalytics.tabs.customers') },
        { id: 'daily',     label: t('challanAnalytics.tabs.dailyTrend') },
        { id: 'list',      label: t('challanAnalytics.tabs.challanList') },
    ];
    const { user } = useAuth();
    const { userRole } = useRole();
    const isSupervisor = userRole === USER_ROLES.DELIVERY_SUPERVISOR;
    const isAdminOrSuper = userRole === USER_ROLES.ADMIN || userRole === USER_ROLES.SUPER_ADMIN;

    const {
        summary, products, productsTotal,
        customers, customersTotal,
        dailyData, challanList, listTotal,
        fetching, error,
        fetchSummary, fetchProducts, fetchCustomers, fetchDaily, fetchList,
    } = useChallanAnalytics();

    const [supervisors, setSupervisors] = useState([]);
    const [supervisorsLoading, setSupervisorsLoading] = useState(false);
    const [selectedSupervisorId, setSelectedSupervisorId] = useState('');

    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [activeTab, setActiveTab] = useState('summary');
    const [status, setStatus] = useState('');
    const [groupBy, setGroupBy] = useState('parent');
    const [challanPrinted, setChallanPrinted] = useState('');
    const [unitType, setUnitType] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const isTabEffectReady = useRef(false);

    const filterRef = useRef({});
    filterRef.current = { selectedSupervisorId, dateRange, status, groupBy, challanPrinted, unitType };

    const buildBaseParams = useCallback(() => {
        const { selectedSupervisorId: sid, dateRange: dr, challanPrinted: cp, unitType: ut } = filterRef.current;
        const p = { from_date: dr.from, to_date: dr.to };
        if (sid) p.supervisor_id = sid;
        if (cp) p.challan_printed = cp;
        if (ut) p.unit_type = ut;
        return p;
    }, []);

    const fetchForTab = useCallback(async (tab, page = 1) => {
        const base = buildBaseParams();
        const { status: sf, groupBy: gb } = filterRef.current;
        const skip = (page - 1) * DEFAULT_PAGE_LIMIT;

        if (tab === 'summary') {
            await fetchSummary(base);
        } else if (tab === 'products') {
            const p = { ...base, group_by: gb, limit: DEFAULT_PAGE_LIMIT, skip };
            if (sf) p.status = sf;
            await fetchProducts(p);
        } else if (tab === 'customers') {
            const p = { ...base, limit: DEFAULT_PAGE_LIMIT, skip };
            if (sf) p.status = sf;
            await fetchCustomers(p);
        } else if (tab === 'daily') {
            await fetchDaily(base);
        } else if (tab === 'list') {
            const p = { ...base, limit: DEFAULT_PAGE_LIMIT, skip };
            if (sf) p.status = sf;
            await fetchList(p);
        }
    }, [buildBaseParams, fetchSummary, fetchProducts, fetchCustomers, fetchDaily, fetchList]);

    // Load supervisors on mount then do initial summary fetch
    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setSupervisorsLoading(true);
            try {
                let initialId = '';
                if (isSupervisor && user?.id) {
                    initialId = user.id;
                    setSelectedSupervisorId(initialId);
                } else if (isAdminOrSuper) {
                    const res = await userService.getDeliverySupervisorsStatus();
                    if (cancelled) return;
                    const list = res.success ? (Array.isArray(res.data) ? res.data : []) : [];
                    setSupervisors(list);
                }

                const dr = getDefaultDateRange();
                const base = { from_date: dr.from, to_date: dr.to };
                if (initialId) base.supervisor_id = initialId;
                await fetchSummary(base);
            } finally {
                if (!cancelled) {
                    setSupervisorsLoading(false);
                    isTabEffectReady.current = true;
                }
            }
        };
        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch when tab changes
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Re-fetch on pagination change (paginated tabs only)
    useEffect(() => {
        if (!isTabEffectReady.current) return;
        if (['products', 'customers', 'list'].includes(activeTab)) {
            fetchForTab(activeTab, currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const handleApply = () => {
        setCurrentPage(1);
        fetchForTab(activeTab, 1);
    };

    const activeTotal = activeTab === 'products' ? productsTotal
        : activeTab === 'customers' ? customersTotal
        : activeTab === 'list' ? listTotal
        : 0;
    const totalPages = Math.ceil(activeTotal / DEFAULT_PAGE_LIMIT) || 1;
    const showPagination = ['products', 'customers', 'list'].includes(activeTab);

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={5}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box sx={{
                        bgcolor: '#189AB4', borderRadius: 2,
                        width: 40, height: 40,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white',
                    }}>
                        <FontAwesomeIcon icon={faFileInvoice} />
                    </Box>
                    <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                        Challan Analytics
                    </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700 }}>
                    Analyze delivery challan performance, product dispatch, customer trends, and daily activity.
                    Warehouse is selected from the sidebar.
                </Typography>
            </Box>

            {/* Filter bar */}
            <ChallanFilter
                supervisors={supervisors}
                supervisorsLoading={supervisorsLoading}
                selectedSupervisorId={selectedSupervisorId}
                onSupervisorChange={setSelectedSupervisorId}
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onFromDateChange={(v) => setDateRange((p) => ({ ...p, from: v }))}
                onToDateChange={(v) => setDateRange((p) => ({ ...p, to: v }))}
                status={status}
                onStatusChange={setStatus}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                challanPrinted={challanPrinted}
                onChallanPrintedChange={setChallanPrinted}
                unitType={unitType}
                onUnitTypeChange={setUnitType}
                onSubmit={handleApply}
                loading={fetching}
                activeTab={activeTab}
                disableSupervisorSelector={isSupervisor}
            />

            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, borderBottom: '2px solid #f3f4f6', flexWrap: 'wrap' }}>
                {TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '10px 20px',
                            border: 'none',
                            borderBottom: activeTab === tab.id ? '3px solid #189AB4' : '3px solid transparent',
                            backgroundColor: 'transparent',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: activeTab === tab.id ? 700 : 500,
                            color: activeTab === tab.id ? '#189AB4' : '#6b7280',
                            transition: 'all 0.15s',
                            marginBottom: -2,
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </Box>

            {/* Error */}
            {error && (
                <Fade in>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {typeof error === 'object' ? JSON.stringify(error) : error}
                    </Alert>
                </Fade>
            )}

            {/* Loading spinner for initial load */}
            {fetching && activeTab === 'summary' && !summary && (
                <Box display="flex" justifyContent="center" p={10}>
                    <CircularProgress size={40} thickness={4} sx={{ color: '#189AB4' }} />
                </Box>
            )}

            {/* Tab content */}
            {activeTab === 'summary' && (
                <Fade in={!!summary}>
                    <div><ChallanSummaryCards summary={summary} /></div>
                </Fade>
            )}

            {activeTab === 'products' && (
                <>
                    <ChallanProductsTable items={products} loading={fetching} groupBy={groupBy} />
                    {showPagination && products?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={activeTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('challanAnalytics.pagination.totalLabels.products')}
                            pageSize={DEFAULT_PAGE_LIMIT}
                        />
                    )}
                </>
            )}

            {activeTab === 'customers' && (
                <>
                    <ChallanCustomersTable items={customers} loading={fetching} />
                    {showPagination && customers?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={activeTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('challanAnalytics.pagination.totalLabels.customers')}
                            pageSize={DEFAULT_PAGE_LIMIT}
                        />
                    )}
                </>
            )}

            {activeTab === 'daily' && (
                <ChallanDailyTable items={dailyData} loading={fetching} />
            )}

            {activeTab === 'list' && (
                <>
                    <ChallanListTable items={challanList} loading={fetching} />
                    {showPagination && challanList?.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalCount={activeTotal}
                            onPageChange={setCurrentPage}
                            loading={fetching}
                            totalLabel={t('challanAnalytics.pagination.totalLabels.challans')}
                            pageSize={DEFAULT_PAGE_LIMIT}
                        />
                    )}
                </>
            )}
        </Container>
    );
};

export default ChallanAnalyticsPage;
